import React, { useState, useEffect } from "react";
import DataTableComponent from "../../../components/Tenant/DataTableComponent/DataTableComponent";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import { CircleLoaderComponent } from "../../../components/Loader/CircleLoaderComponent";
import toast from "react-hot-toast";
import { useLazyFetchIffcoInventorySummaryQuery } from "../../../features/tenant/Iffco/IffcoApi";

const IffcoRetailerInventory = () => {
  // Filters
  const [asOnDate, setAsOnDate] = useState(new Date().toISOString().split("T")[0]);
  const [retailerCode, setRetailerCode] = useState("");
  const [productCode, setProductCode] = useState("");
  const [batchCode, setBatchCode] = useState("");
  const [stateFilter, setStateFilter] = useState("");
  const [districtFilter, setDistrictFilter] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [tableData, setTableData] = useState([]);
  const [totalRows, setTotalRows] = useState(0);

  // RTK Query hook
  const [triggerInventory, { isLoading }] = useLazyFetchIffcoInventorySummaryQuery();

  const getParams = () => {
    const vendorData = JSON.parse(sessionStorage.getItem("vendorData") || "{}");
    const token = sessionStorage.getItem("access_token");
    return { tenant_id: vendorData.tenant_id, token };
  };

  const loadData = async () => {
    const { tenant_id, token } = getParams();
    if (!tenant_id || !token) return;

    const queryParams = {
      page: currentPage,
      limit,
      as_on_date: asOnDate,
      retailer_code: retailerCode,
      product_code: productCode,
      batch_code: batchCode,
      state: stateFilter,
      district: districtFilter,
    };

    Object.keys(queryParams).forEach((key) => {
      if (!queryParams[key]) delete queryParams[key];
    });

    try {
      const res = await triggerInventory({ tenant_id, token, query: queryParams }).unwrap();
      if (res) {
        const data = res.body?.data || res.data || [];
        const total = Number(res.body?.total_count ?? res.body?.total ?? res.total_count ?? res.total ?? data.length);
        setTableData(data);
        setTotalRows(total);
      } else {
        setTableData([]);
        setTotalRows(0);
      }
    } catch (err) {
      console.error("Error loading Retailer Inventory data:", err);
      toast.error(err?.data?.message || "Failed to load report data");
    }
  };

  useEffect(() => {
    loadData();
  }, [currentPage, limit]);

  const handleApplyFilter = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    loadData();
  };

  const handleResetFilter = () => {
    setAsOnDate(new Date().toISOString().split("T")[0]);
    setRetailerCode("");
    setProductCode("");
    setBatchCode("");
    setStateFilter("");
    setDistrictFilter("");
    setCurrentPage(1);
    setTimeout(loadData, 50);
  };

  const getColumns = () => {
    const offset = (currentPage - 1) * limit;
    return [
      { name: "S.No", cell: (row, idx) => <span className="fw-semibold text-secondary">{offset + idx + 1}</span>, width: "65px" },
      { name: "Retailer Code", selector: (row) => row.retailer_code || "-", sortable: true, minWidth: "135px", wrap: true },
      { name: "Retailer Name", selector: (row) => row.retailer_name || "-", sortable: true, minWidth: "150px", wrap: true },
      { name: "Mobile", selector: (row) => row.mobile || "-", sortable: true, minWidth: "120px" },
      { name: "State", selector: (row) => row.state || "-", sortable: true, minWidth: "120px", wrap: true },
      { name: "District", selector: (row) => row.district || "-", sortable: true, minWidth: "120px", wrap: true },
      { name: "Product Code", selector: (row) => row.product_code || "-", sortable: true, minWidth: "130px", wrap: true },
      { name: "Product Name", selector: (row) => row.product_name || "-", sortable: true, minWidth: "160px", wrap: true },
      { name: "Batch Code", selector: (row) => row.batch_code || "-", sortable: true, minWidth: "130px", wrap: true },
      { name: "QR Code", selector: (row) => row.unique_code || row.qr_code || "-", sortable: true, minWidth: "140px", wrap: true },
      { name: "Codes In Hand", selector: (row) => <span className="badge bg-primary px-2.5 py-1">{row.codes_in_hand || 0}</span>, sortable: true, minWidth: "135px" },
      { name: "Scan Date", selector: (row) => row.first_scan_date ? new Date(row.first_scan_date).toLocaleString() : "-", sortable: true, minWidth: "160px" },
    ];
  };

  return (
    <div className="container-fluid py-3">
      <LinksComponent mainLink="IFFCO Reports" subLink="Retailer Inventory Summary" />

      {/* Filter Card Section */}
      <div className="card border shadow-sm mb-4 rounded-3">
        <div className="card-header bg-white py-3 border-bottom d-flex align-items-center justify-content-between">
          <div className="d-flex align-items-center gap-2">
            <i className="bi bi-funnel text-primary fs-6"></i>
            <h6 className="m-0 fw-bold text-dark">Filter Reports</h6>
          </div>
          <span className="badge bg-light text-secondary border font-normal">Filter Criteria</span>
        </div>
        <div className="card-body bg-light bg-opacity-25 p-3">
          <form onSubmit={handleApplyFilter} className="row g-3 align-items-end">
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">As-On Date</label>
              <input
                type="date"
                className="form-control form-control-sm border-gray-300 rounded"
                value={asOnDate}
                onChange={(e) => setAsOnDate(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">Retailer Code</label>
              <input
                type="text"
                className="form-control form-control-sm border-gray-300 rounded"
                placeholder="Enter Retailer Code"
                value={retailerCode}
                onChange={(e) => setRetailerCode(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">Product Code</label>
              <input
                type="text"
                className="form-control form-control-sm border-gray-300 rounded"
                placeholder="Enter Product Code"
                value={productCode}
                onChange={(e) => setProductCode(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">Batch Code</label>
              <input
                type="text"
                className="form-control form-control-sm border-gray-300 rounded"
                placeholder="Enter Batch Code"
                value={batchCode}
                onChange={(e) => setBatchCode(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">State</label>
              <input
                type="text"
                className="form-control form-control-sm border-gray-300 rounded"
                placeholder="Enter State"
                value={stateFilter}
                onChange={(e) => setStateFilter(e.target.value)}
              />
            </div>
            <div className="col-md-3 col-sm-6">
              <label className="form-label text-dark fw-medium small mb-1">District</label>
              <input
                type="text"
                className="form-control form-control-sm border-gray-300 rounded"
                placeholder="Enter District"
                value={districtFilter}
                onChange={(e) => setDistrictFilter(e.target.value)}
              />
            </div>

            <div className="col-12 d-flex gap-2 justify-content-end pt-2">
              <button
                type="button"
                className="btn btn-outline-secondary btn-sm px-4 fw-medium"
                onClick={handleResetFilter}
              >
                Reset
              </button>
              <button type="submit" className="btn btn-primary btn-sm px-4 fw-medium shadow-sm">
                Apply Filter
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Data Table */}
      <div className="card shadow-sm">
        <div className="card-body p-0">
          {isLoading ? (
            <div className="py-5">
              <CircleLoaderComponent />
            </div>
          ) : (
            <DataTableComponent
              columns={getColumns()}
              data={tableData}
              totalRows={totalRows}
              paginationDefaultPage={currentPage}
              paginationPerPage={limit}
              handlePageChange={(page) => setCurrentPage(page)}
              handlePerRowsChange={(newLimit) => setLimit(newLimit)}
            />
          )}
        </div>
      </div>
    </div>
  );
};

export default IffcoRetailerInventory;
